import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Phone, ClipboardCheck } from "lucide-react";
import { api } from "../../lib/api";

const MONTH_NAMES = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTH_MAP = {
  janvier: 0, fevrier: 1, "février": 1, mars: 2, avril: 3, mai: 4, juin: 5,
  juillet: 6, aout: 7, "août": 7, septembre: 8, octobre: 9, novembre: 10, decembre: 11, "décembre": 11,
};

function parseFrDate(str) {
  if (!str) return null;
  const m = str.toLowerCase().match(/(\d{1,2})\s+([a-zûéôèàäîç]+)\s+(\d{4})(?:[^\d]*(\d{1,2}):(\d{2}))?/);
  if (!m) return null;
  const day = parseInt(m[1], 10);
  const mon = MONTH_MAP[m[2]];
  const year = parseInt(m[3], 10);
  if (mon === undefined) return null;
  return { date: new Date(year, mon, day), time: m[4] ? `${m[4]}:${m[5]}` : null };
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function AdminCalendar() {
  const [clients, setClients] = useState([]);
  const navigate = useNavigate();

  useEffect(() => { api.get("/admin/clients").then(({ data }) => setClients(data)).catch(() => {}); }, []);

  const events = useMemo(() => {
    const list = [];
    clients.forEach((c) => {
      const call = parseFrDate(c.next_call);
      if (call) list.push({ date: call.date, time: call.time, type: "call", client: c });
      const rev = parseFrDate(c.next_review);
      if (rev) list.push({ date: rev.date, time: rev.time, type: "review", client: c });
    });
    return list;
  }, [clients]);

  const [cursor, setCursor] = useState(null);
  useEffect(() => {
    if (cursor || events.length === 0) return;
    const earliest = events.map((e) => e.date).sort((a, b) => a - b)[0];
    setCursor(new Date(earliest.getFullYear(), earliest.getMonth(), 1));
  }, [events, cursor]);

  const view = cursor || new Date();
  const year = view.getFullYear();
  const month = view.getMonth();
  const today = new Date();

  const cells = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const startOffset = (firstDay.getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < startOffset; i++) arr.push(null);
    for (let d = 1; d <= daysInMonth; d++) arr.push(new Date(year, month, d));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [year, month]);

  const tKey = (t) => (t ? t.padStart(5, "0") : "99:99");
  const eventsFor = (date) => events.filter((e) => sameDay(e.date, date)).sort((a, b) => tKey(a.time).localeCompare(tKey(b.time)));

  const go = (delta) => setCursor(new Date(year, month + delta, 1));
  const circle = "flex h-9 w-9 items-center justify-center rounded-full border border-ink/80 text-ink transition-colors hover:bg-ink hover:text-white";

  return (
    <div className="max-w-6xl space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-sans text-3xl font-medium text-ink sm:text-4xl">Mon calendrier</h1>
          <p className="mt-2 font-light text-ink-soft">Appels, bilans et échéances de tes clientes</p>
        </div>
      </div>

      {/* Month grid */}
      <div className="rounded-[28px] border border-line bg-white p-4 shadow-card sm:p-6">
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <button data-testid="cal-prev" onClick={() => go(-1)} className={circle}><ChevronLeft size={16} /></button>
          <button data-testid="cal-today" onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))} className="rounded-full border border-ink/80 px-4 py-2 text-xs font-medium text-ink transition-colors hover:bg-ink hover:text-white">Aujourd'hui</button>
          <button data-testid="cal-next" onClick={() => go(1)} className={circle}><ChevronRight size={16} /></button>
          <p data-testid="cal-title" className="ml-2 font-sans text-lg font-medium text-ink">{MONTH_NAMES[month]} {year}</p>
        </div>
        <div className="overflow-x-auto">
        <div className="grid min-w-[760px] grid-cols-7 gap-2">
          {WEEKDAYS.map((d) => (
            <div key={d} className="pb-1 text-center text-xs font-medium text-ink-muted">{d}</div>
          ))}
          {cells.map((date, i) => {
            const isToday = date && sameDay(date, today);
            const dayEvents = date ? eventsFor(date) : [];
            return (
              <div
                key={i}
                data-testid={date ? `cal-day-${date.getDate()}` : `cal-empty-${i}`}
                className={`min-h-[124px] rounded-2xl p-2 ${date ? "bg-[#F5F0EA]" : "bg-[#F5F0EA]/40"} ${isToday ? "ring-2 ring-bordeaux" : ""}`}
              >
                {date && (
                  <>
                    <span className={`mb-2 inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-xs font-medium ${isToday ? "bg-bordeaux text-white" : "text-ink-muted"}`}>
                      {date.getDate()}
                    </span>
                    <div className="space-y-1.5">
                      {dayEvents.map((e, idx) => (
                        <button
                          key={idx}
                          onClick={() => navigate(`/admin/clientes/${e.client.id}`)}
                          data-testid={`cal-event-${e.client.id}-${e.type}`}
                          className="block w-full rounded-xl bg-white p-2.5 text-left shadow-[0_2px_10px_rgba(45,24,30,0.06)] transition-transform hover:-translate-y-0.5"
                        >
                          <span className="line-clamp-2 text-xs font-medium leading-snug text-ink">{[e.client.first_name, e.client.last_name].filter(Boolean).join(" ")}</span>
                          <span className="mt-2 flex items-center gap-1 truncate rounded-full bg-bordeaux-tint px-2 py-0.5 text-[10px] font-medium text-bordeaux">
                            {e.type === "call" ? <Phone size={10} className="shrink-0" /> : <ClipboardCheck size={10} className="shrink-0" />}
                            {e.type === "call" ? "Appel" : "Bilan"}{e.time ? ` · ${e.time}` : ""}
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
        </div>
      </div>
    </div>
  );
}
